// useSaveDataMode — should the UI defer non-essential fetches?
//
// Resolves to `true` when the user has signalled they want reduced
// data usage, via any of:
//   - `navigator.connection.saveData` (the OS / browser hint set by
//      "Lite mode" on mobile Chrome and similar)
//   - `users.settings.reduceDataMode === true` (explicit app opt-in)
//
// Resolves to `false` when:
//   - the user explicitly opted out (`reduceDataMode === false`),
//      regardless of the OS hint, OR
//   - neither signal is present.
//
// The OS-level hint is dynamic on some platforms — we listen for the
// `change` event on `navigator.connection` so a user who toggles Lite
// mode mid-session sees the UI adjust on next render.

import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

interface NetworkInformationLike {
  saveData?: boolean;
  addEventListener?: (event: 'change', listener: () => void) => void;
  removeEventListener?: (event: 'change', listener: () => void) => void;
}

function getConnection(): NetworkInformationLike | null {
  if (typeof navigator === 'undefined') return null;
  // The API is not on the standard Navigator type; cast through
  // unknown to keep this fully typed without `any`.
  const conn = (navigator as unknown as { connection?: NetworkInformationLike }).connection;
  return conn ?? null;
}

function readOsSaveData(): boolean {
  return Boolean(getConnection()?.saveData);
}

/**
 * Returns true when the SPA should defer non-essential fetches. Pair
 * with `useApi(saveData ? null : '/api/...')` to gate a request.
 *
 * Safety surfaces (doses, taper plan, crisis windows, clinical
 * recommendation) should NEVER be gated by this hook — they're the
 * load-bearing fold of the Today screen and must load eagerly even
 * on a metered connection.
 */
export function useSaveDataMode(): boolean {
  const { me } = useAuth();
  const userPref = me?.settings.reduceDataMode;

  const [osSaveData, setOsSaveData] = useState<boolean>(() => readOsSaveData());

  useEffect(() => {
    const conn = getConnection();
    if (!conn?.addEventListener) return;
    const onChange = () => setOsSaveData(readOsSaveData());
    conn.addEventListener('change', onChange);
    return () => {
      conn.removeEventListener?.('change', onChange);
    };
  }, []);

  // Explicit user pref overrides the OS hint in either direction:
  //   - `true`  → reduce regardless of OS hint
  //   - `false` → don't reduce even if OS hint says save data
  //   - undefined → follow OS hint
  if (userPref === true) return true;
  if (userPref === false) return false;
  return osSaveData;
}
